import React, { useEffect, useState } from "react";
import { Box, Chip, Divider, Tooltip, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import TuneIcon from "@mui/icons-material/Tune";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { COLORS } from "../assets/theme";
import CustomDialog from "./common/CustomDialog";
import CustomTable from "./common/table/CustomTable";
import {
  IRepricingSession,
  RepricingSessionStatus,
  useRepricingSessionsQuery,
} from "../services/MessageTrackerService";
import { fetchClient } from "../services/ClientService";
import { fetchCountry } from "../services/CountryService";
import { fetchOperator } from "../services/OperatorsService";
import { fetchProvider } from "../services/ProviderService";
import { IAction, IField, Params } from "../types/table/interfaces.type";
import { hasJwtPermission } from "../services/ServicesHelpers";

const STATUS_CONFIG: Record<
  RepricingSessionStatus,
  { label: string; icon: React.ReactNode; chipBg: string; chipColor: string; bannerBg: string; bannerColor: string }
> = {
  [RepricingSessionStatus.RUNNING]: {
    label: "Running",
    icon: <HourglassEmptyIcon sx={{ fontSize: 16 }} />,
    chipBg: "#fff3cd",
    chipColor: "#856404",
    bannerBg: "#fffbeb",
    bannerColor: "#92620a",
  },
  [RepricingSessionStatus.DONE]: {
    label: "Done",
    icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
    chipBg: "#d4edda",
    chipColor: "#155724",
    bannerBg: "#f0faf2",
    bannerColor: "#155724",
  },
  [RepricingSessionStatus.ERROR]: {
    label: "Error",
    icon: <ErrorOutlineIcon sx={{ fontSize: 16 }} />,
    chipBg: "#f8d7da",
    chipColor: "#721c24",
    bannerBg: "#fff5f5",
    bannerColor: "#721c24",
  },
};

const StatusChip: React.FC<{ status: RepricingSessionStatus }> = ({ status }) => {
  const cfg = STATUS_CONFIG[status];
  if (!cfg) return <Typography variant="body2">{status ?? "—"}</Typography>;
  return (
    <Chip
      icon={cfg.icon as React.ReactElement}
      label={cfg.label}
      size="small"
      clickable={false}
      sx={{
        backgroundColor: cfg.chipBg,
        color: cfg.chipColor,
        fontWeight: 600,
        pointerEvents: "none",
        "& .MuiChip-icon": { color: cfg.chipColor },
      }}
    />
  );
};

const SectionTitle: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <Box display="flex" alignItems="center" gap={0.75} mb={0.75}>
    <Box sx={{ color: COLORS.GOLD, display: "flex", alignItems: "center" }}>{icon}</Box>
    <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.typographyColor, letterSpacing: 0.3, fontSize: "0.72rem", textTransform: "uppercase" }}>
      {title}
    </Typography>
  </Box>
);

const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ flex: "1 1 30%", minWidth: 90 }}>
    <Typography
      variant="caption"
      sx={{ color: COLORS.placeHolderColor, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, fontSize: "0.62rem", lineHeight: 1.2, display: "block" }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ mt: 0.1, color: COLORS.typographyColor, wordBreak: "break-word", fontSize: "0.8rem" }}>
      {value ?? "—"}
    </Typography>
  </Box>
);

const FieldRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box display="flex" flexWrap="wrap" rowGap={1} columnGap={0.75}>
    {children}
  </Box>
);

const Section: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      px: 1,
      py: 1,
      borderRadius: 1.5,
      border: `1px solid ${COLORS.dividerColor}`,
      backgroundColor: COLORS.White,
      display: "flex",
      flexDirection: "column",
      gap: 1,
    }}
  >
    {children}
  </Box>
);

const formatDate = (d?: string | null) =>
  d ? new Date(d).toLocaleString() : "—";

interface ResolvedNames {
  connectionProfile: string;
  vendor: string;
  country: string;
  operator: string;
}

function useSessionNames(session: IRepricingSession | null): ResolvedNames {
  const [names, setNames] = useState<ResolvedNames>({
    connectionProfile: "",
    vendor: "",
    country: "",
    operator: "",
  });

  const sessionId = session?.id;
  const cpId = session?.connectionProfileId;
  const vendorId = session?.vendorId;
  const countryId = session?.destinationCountryId;
  const operatorId = session?.destinationOperatorId;

  useEffect(() => {
    if (!sessionId) return;
    setNames({ connectionProfile: "", vendor: "", country: "", operator: "" });

    const noId = new Error("No ID");
    const fetchAll = async () => {
      const [cp, vendor, country, operator] = await Promise.allSettled([
        cpId ? fetchClient(String(cpId)) : Promise.reject(noId),
        vendorId ? fetchProvider(String(vendorId)) : Promise.reject(noId),
        countryId ? fetchCountry(countryId) : Promise.reject(noId),
        operatorId ? fetchOperator(operatorId) : Promise.reject(noId),
      ]);

      setNames({
        connectionProfile: cp.status === "fulfilled" ? cp.value.name : "",
        vendor: vendor.status === "fulfilled" ? vendor.value.name : "",
        country: country.status === "fulfilled" ? country.value.name : "",
        operator: operator.status === "fulfilled" ? operator.value.name : "",
      });
    };

    fetchAll();
  }, [sessionId, cpId, vendorId, countryId, operatorId]);

  return names;
}

const resolvedLabel = (name: string, id?: number | null) => {
  if (!id) return "—";
  return name || String(id);
};

const SessionDetailDialog: React.FC<{
  session: IRepricingSession | null;
  open: boolean;
  onClose: () => void;
}> = ({ session, open, onClose }) => {
  const names = useSessionNames(session);

  if (!session) return null;

  const cfg = STATUS_CONFIG[session.repricingStatus];
  const hasCriteria =
    session.criteria &&
    Object.values(session.criteria).some((v) =>
      Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null
    );

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      title={`Repricing Session`}
      mode="history"
      dialogSx={{ minWidth: "480px", maxWidth: "480px" }}
      fields={[
        {
          type: "custom",
          component: (
            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1 }}>

              {/* Status banner */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1,
                  py: 0.75,
                  borderRadius: 1.5,
                  backgroundColor: cfg?.bannerBg ?? COLORS.lightGrayBackground,
                  border: `1px solid ${cfg?.chipBg ?? COLORS.dividerColor}`,
                }}
              >
                <Box sx={{ color: cfg?.bannerColor, display: "flex" }}>{cfg?.icon}</Box>
                <Box flex={1}>
                  <Typography sx={{ color: COLORS.placeHolderColor, fontSize: "0.62rem", textTransform: "uppercase", letterSpacing: 0.5, lineHeight: 1.2 }}>
                    Status
                  </Typography>
                  <Typography sx={{ fontWeight: 700, color: cfg?.bannerColor, fontSize: "0.85rem", lineHeight: 1.3 }}>
                    {cfg?.label ?? session.repricingStatus}
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ textAlign: "center", px: 0.5 }}>
                  <Typography sx={{ color: COLORS.placeHolderColor, fontSize: "0.62rem", textTransform: "uppercase", letterSpacing: 0.5, lineHeight: 1.2 }}>
                    Updated CDRs
                  </Typography>
                  <Typography sx={{ fontWeight: 700, color: COLORS.typographyColor, fontSize: "1.1rem", lineHeight: 1.3 }}>
                    {session.updatedCount ?? 0}
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ textAlign: "center", px: 0.5 }}>
                  <Typography sx={{ color: COLORS.placeHolderColor, fontSize: "0.62rem", textTransform: "uppercase", letterSpacing: 0.5, lineHeight: 1.2 }}>
                    New Rate
                  </Typography>
                  <Typography sx={{ fontWeight: 700, color: COLORS.typographyColor, fontSize: "0.85rem", lineHeight: 1.3 }}>
                    {session.newRate ?? "—"}
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ textAlign: "center", px: 0.5 }}>
                  <Typography sx={{ color: COLORS.placeHolderColor, fontSize: "0.62rem", textTransform: "uppercase", letterSpacing: 0.5, lineHeight: 1.2 }}>
                    New Cost
                  </Typography>
                  <Typography sx={{ fontWeight: 700, color: COLORS.typographyColor, fontSize: "0.85rem", lineHeight: 1.3 }}>
                    {session.newCost ?? "—"}
                  </Typography>
                </Box>
              </Box>

              {/* Error banner */}
              {session.errorMessage && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 0.75,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1.5,
                    backgroundColor: "#fff5f5",
                    border: "1px solid #f8d7da",
                  }}
                >
                  <ErrorOutlineIcon sx={{ color: "#721c24", fontSize: 16, mt: 0.2, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: "#721c24", wordBreak: "break-word", fontSize: "0.78rem" }}>
                    {session.errorMessage}
                  </Typography>
                </Box>
              )}

              {/* Details */}
              <Section>
                <SectionTitle icon={<CalendarTodayIcon sx={{ fontSize: 14 }} />} title="Date Range" />
                <FieldRow>
                  <Field label="From Date" value={formatDate(session.fromDate)} />
                  <Field label="To Date" value={formatDate(session.toDate)} />
                </FieldRow>
                <Divider sx={{ my: 0.25 }} />
                <SectionTitle icon={<TuneIcon sx={{ fontSize: 14 }} />} title="Applied Filters" />
                <FieldRow>
                  <Field label="Connection Profile" value={resolvedLabel(names.connectionProfile, session.connectionProfileId)} />
                  <Field label="Vendor" value={resolvedLabel(names.vendor, session.vendorId)} />
                  <Field label="Country" value={resolvedLabel(names.country, session.destinationCountryId)} />
                  <Field label="Operator" value={resolvedLabel(names.operator, session.destinationOperatorId)} />
                  <Field label="CDR Status" value={session.status || "—"} />
                  <Field label="DLR Status" value={session.dlrStatus || "—"} />
                  <Field label="Search" value={session.search || "—"} />
                </FieldRow>
                <Divider sx={{ my: 0.25 }} />
                <SectionTitle icon={<PersonOutlineIcon sx={{ fontSize: 14 }} />} title="Metadata" />
                <FieldRow>
                  <Field label="Created By" value={session.createdBy || "—"} />
                  <Field label="Created Date" value={formatDate(session.createdDate)} />
                </FieldRow>
              </Section>

              {/* Criteria */}
              {hasCriteria && (
                <Section>
                  <SectionTitle icon={<InfoOutlinedIcon sx={{ fontSize: 14 }} />} title="Criteria" />
                  <FieldRow>
                    {Object.entries(session.criteria).map(([key, val]) => {
                      if (val === undefined || val === null || (Array.isArray(val) && val.length === 0))
                        return null;
                      let display: React.ReactNode;
                      if (Array.isArray(val)) {
                        display = val.join(", ");
                      } else if (typeof val === "boolean") {
                        display = val ? "Yes" : "No";
                      } else {
                        display = val;
                      }
                      return <Field key={key} label={key} value={display} />;
                    })}
                  </FieldRow>
                </Section>
              )}

            </Box>
          ),
        },
      ]}
      buttons={[]}
    />
  );
};

const SESSIONS_FILTERS: Params<IRepricingSession> = { sort: "createdDate,desc" };

const SESSION_FIELDS: IField<IRepricingSession>[] = [
  {
    key: "repricingStatus",
    header: "Status",
    transform: (row: IRepricingSession) => (
      <StatusChip status={row.repricingStatus} />
    ),
  },
  { key: "updatedCount", header: "Updated Count" },
  { key: "newRate", header: "New Rate" },
  { key: "newCost", header: "New Cost" },
  {
    key: "fromDate",
    header: "From Date",
    transform: (row: IRepricingSession) => formatDate(row.fromDate),
  },
  {
    key: "toDate",
    header: "To Date",
    transform: (row: IRepricingSession) => formatDate(row.toDate),
  },
  { key: "createdBy", header: "Created By" },
  {
    key: "createdDate",
    header: "Created Date",
    transform: (row: IRepricingSession) => formatDate(row.createdDate),
  },
];

const viewActionIcon = () => (
  <Tooltip title="View Details">
    <VisibilityIcon sx={{ color: hasJwtPermission("GET_REPRICING_SESSION") ? COLORS.GOLD : "#D3D3D3", width: 23 }} />
  </Tooltip>
);

const RepricingSessionsTable: React.FC = () => {
  const [selectedSession, setSelectedSession] =
    useState<IRepricingSession | null>(null);
  const [viewOpen, setViewOpen] = useState(false);

  const actions: IAction<IRepricingSession>[] = [
    {
      id: "view",
      label: "View",
      name: "view",
      icon: viewActionIcon,
      disabled: !hasJwtPermission("GET_REPRICING_SESSION"),
      handler: (row: IRepricingSession) => {
        setSelectedSession(row);
        setViewOpen(true);
      },
    },
  ];

  return (
    <>
      <CustomTable<IRepricingSession>
        filters={SESSIONS_FILTERS}
        fields={SESSION_FIELDS}
        fetchFunc={useRepricingSessionsQuery}
        actions={actions}
        sortableFields={["repricingStatus", "createdDate", "createdBy", "updatedCount"]}
        shadeRow={(row) => row.repricingStatus === RepricingSessionStatus.RUNNING}
        shadeBg="#fff8e1"
      />

      <SessionDetailDialog
        session={selectedSession}
        open={viewOpen}
        onClose={() => setViewOpen(false)}
      />
    </>
  );
};

export default RepricingSessionsTable;
